import { h } from 'vue';
import type { ItemType } from 'ant-design-vue';
import type { AdminPermission } from 'shared';

export interface AdminNavigationItem {
  key: string;
  label: string;
  routeName: string;
  icon: string;
  permission?: AdminPermission;
}

export interface AdminNavigationGroup {
  key: string;
  label: string;
  icon: string;
  children: AdminNavigationItem[];
}

export const adminNavigation: AdminNavigationGroup[] = [
  {
    key: 'identity',
    label: '用户与空间',
    icon: 'ri-team-line',
    children: [
      {
        key: 'users',
        label: '用户',
        routeName: 'admin-users',
        icon: 'ri-user-3-line',
        permission: 'user.read',
      },
      {
        key: 'workspaces',
        label: '工作台',
        routeName: 'admin-workspaces',
        icon: 'ri-layout-grid-line',
        permission: 'workspace.read',
      },
    ],
  },
  {
    key: 'marketplace',
    label: '能力商店',
    icon: 'ri-store-2-line',
    children: [
      {
        key: 'marketplace-reviews',
        label: '审核队列',
        routeName: 'admin-marketplace-reviews',
        icon: 'ri-shield-check-line',
        permission: 'marketplace.review',
      },
      {
        key: 'marketplace-assets',
        label: '资产管理',
        routeName: 'admin-marketplace-assets',
        icon: 'ri-apps-2-line',
        permission: 'marketplace.read',
      },
      {
        key: 'marketplace-collections',
        label: '专题运营',
        routeName: 'admin-marketplace-collections',
        icon: 'ri-layout-masonry-line',
        permission: 'marketplace.curate',
      },
      {
        key: 'marketplace-analytics',
        label: '运营分析',
        routeName: 'admin-marketplace-analytics',
        icon: 'ri-line-chart-line',
        permission: 'marketplace.read',
      },
    ],
  },
  {
    key: 'commerce',
    label: '交易中心',
    icon: 'ri-bank-card-line',
    children: [
      {
        key: 'products',
        label: '商品',
        routeName: 'admin-products',
        icon: 'ri-price-tag-3-line',
        permission: 'commerce.read',
      },
      {
        key: 'coupons',
        label: '优惠与兑换券',
        routeName: 'admin-coupons',
        icon: 'ri-coupon-3-line',
        permission: 'commerce.read',
      },
      {
        key: 'orders',
        label: '订单',
        routeName: 'admin-orders',
        icon: 'ri-file-list-3-line',
        permission: 'commerce.read',
      },
      {
        key: 'refunds',
        label: '退款',
        routeName: 'admin-refunds',
        icon: 'ri-refund-2-line',
        permission: 'commerce.read',
      },
      {
        key: 'settlements',
        label: '结算',
        routeName: 'admin-settlements',
        icon: 'ri-exchange-dollar-line',
        permission: 'settlement.manage',
      },
      {
        key: 'payment-attempts',
        label: '支付流水',
        routeName: 'admin-payment-attempts',
        icon: 'ri-secure-payment-line',
        permission: 'commerce.read',
      },
      {
        key: 'payment-settings',
        label: '支付设置',
        routeName: 'admin-payment-settings',
        icon: 'ri-settings-4-line',
        permission: 'commerce.read',
      },
      {
        key: 'subscriptions',
        label: '自动续费',
        routeName: 'admin-subscriptions',
        icon: 'ri-repeat-2-line',
        permission: 'commerce.read',
      },
    ],
  },
  {
    key: 'governance',
    label: '平台治理',
    icon: 'ri-shield-star-line',
    children: [
      {
        key: 'monitor',
        label: '系统监控',
        routeName: 'admin-monitor',
        icon: 'ri-pulse-line',
        permission: 'dashboard.read',
      },
      {
        key: 'system-services',
        label: '系统服务测试',
        routeName: 'admin-system-services',
        icon: 'ri-server-line',
        permission: 'dashboard.read',
      },
      {
        key: 'message-reviews',
        label: '消息审查',
        routeName: 'admin-message-reviews',
        icon: 'ri-shield-search-line',
        permission: 'message_review.read',
      },
      {
        key: 'notifications',
        label: '通知记录',
        routeName: 'admin-notifications',
        icon: 'ri-notification-3-line',
        permission: 'dashboard.read',
      },
      {
        key: 'audits',
        label: '审计日志',
        routeName: 'admin-audits',
        icon: 'ri-shield-keyhole-line',
        permission: 'audit.read',
      },
    ],
  },
  {
    key: 'settings',
    label: '平台设置',
    icon: 'ri-settings-3-line',
    children: [
      {
        key: 'admins',
        label: '管理员与角色',
        routeName: 'admin-admins',
        icon: 'ri-admin-line',
        permission: 'admin.manage',
      },
      {
        key: 'system-models',
        label: '系统模型',
        routeName: 'admin-system-models',
        icon: 'ri-brain-line',
        permission: 'settings.read',
      },
      {
        key: 'growth',
        label: '增长与套餐额度',
        routeName: 'admin-growth',
        icon: 'ri-rocket-2-line',
        permission: 'settings.read',
      },
      {
        key: 'creative-media',
        label: '创意中心',
        routeName: 'admin-creative-media',
        icon: 'ri-sparkling-2-line',
        permission: 'settings.read',
      },
      {
        key: 'advertising',
        label: '广告运营',
        routeName: 'admin-advertising',
        icon: 'ri-advertisement-line',
        permission: 'advertising.read',
      },
      {
        key: 'settings',
        label: '系统配置',
        routeName: 'admin-settings',
        icon: 'ri-equalizer-2-line',
        permission: 'settings.read',
      },
      {
        key: 'client-releases',
        label: '客户端版本',
        routeName: 'admin-client-releases',
        icon: 'ri-download-cloud-2-line',
        permission: 'settings.read',
      },
      {
        key: 'cms-content',
        label: '内容中心',
        routeName: 'admin-cms-content',
        icon: 'ri-article-line',
        permission: 'cms.read',
      },
    ],
  },
];

export function toMenuItems(
  can: (permission?: AdminPermission) => boolean,
  pendingMessageReviews = 0,
): ItemType[] {
  return [
    ...(can('dashboard.read')
      ? [{ key: 'admin-dashboard', icon: h('i', { class: 'ri-home-5-line' }), label: '首页' }]
      : []),
    ...adminNavigation
      .map((group) => ({
        key: group.key,
        icon: h('i', { class: group.icon }),
        label: group.label,
        children: group.children
          .filter((item) => can(item.permission))
          .map((item) => ({
            key: item.routeName,
            icon: h('i', { class: item.icon }),
            label:
              item.routeName === 'admin-message-reviews' && pendingMessageReviews > 0
                ? h('span', { class: 'admin-menu-label' }, [
                    item.label,
                    h(
                      'em',
                      { class: 'admin-menu-badge' },
                      pendingMessageReviews > 99 ? '99+' : String(pendingMessageReviews),
                    ),
                  ])
                : item.label,
          })),
      }))
      .filter((group) => group.children.length > 0),
  ];
}
